import axiosClient from './axiosClient';

import { API_ENDPOINTS } from '@/constants';

/**
 * MoonPay API — headless Platform (deposit / KYC / session) + off-ramp helpers.
 * The compliance-sensitive steps (auth, buy execution) run in MoonPay frames on
 * the client (see lib/moonpay); these endpoints proxy the server-side REST work.
 */

export interface MoonpayConfig {
  publishableKey: string;
  isTest: boolean;
  defaultFiat: string;
  cryptoAsset: string;
  /** ISO alpha-2 countries that use our own (headless) Customer-API KYC screens. */
  headlessCountries: string[];
}

export interface MoonpayKycState {
  customerId: string | null;
  status: string; // MoonPay: not_created | collecting | verifying | active | unavailable
  kycStatus: string; // ours: none | action_required | under_review | verified | rejected
  requirements: Record<string, any>;
  challenge: any;
}

export interface MoonpayPaymentMethod {
  type?: string;
  name?: string;
  minAmount?: number;
  maxAmount?: number;
  minBuyAmount?: number;
  maxBuyAmount?: number;
  [k: string]: any;
}

export const moonpayApi = {
  getConfig: async (): Promise<MoonpayConfig> => {
    const { data } = await axiosClient.get<{ data: MoonpayConfig }>(API_ENDPOINTS.MOONPAY.CONFIG);
    return data.data;
  },

  /** Supported countries with buy/sell flags (catalog — no quote brute-forcing). */
  getCountries: async (): Promise<any[]> => {
    const { data } = await axiosClient.get<{ data: any[] }>(API_ENDPOINTS.MOONPAY.COUNTRIES);
    return data.data;
  },

  /** Supported fiat + crypto currencies with min/max limits. */
  getCurrencies: async (): Promise<any[]> => {
    const { data } = await axiosClient.get<{ data: any[] }>(API_ENDPOINTS.MOONPAY.CURRENCIES);
    return data.data;
  },

  /** Pre-auth min/max limits for a fiat (+ optional crypto) from /v3/currencies. */
  getLimits: async (
    fiat: string,
    crypto?: string,
  ): Promise<{
    fiat: string;
    crypto: string | null;
    minBuyAmount: number | null;
    maxBuyAmount: number | null;
    minSellAmount: number | null;
    maxSellAmount: number | null;
  }> => {
    const { data } = await axiosClient.get<{ data: any }>(API_ENDPOINTS.MOONPAY.LIMITS, {
      params: { fiat, ...(crypto ? { crypto } : {}) },
    });
    return data.data;
  },

  /**
   * Payment methods + per-method limits for the authenticated customer's region.
   * NOTE: customer-scoped — pass the customer `accessToken` (from the frame creds);
   * the server key is rejected. Only call this AFTER the customer connects.
   */
  getPaymentMethods: async (params: {
    accessToken: string;
    country?: string;
    currencyCode?: string;
    baseCurrencyCode?: string;
    type?: 'buy' | 'sell';
  }): Promise<MoonpayPaymentMethod[]> => {
    const { data } = await axiosClient.get<{ data: MoonpayPaymentMethod[] }>(
      API_ENDPOINTS.MOONPAY.PAYMENT_METHODS,
      { params },
    );
    return data.data;
  },

  createSession: async (
    deviceIp?: string,
    termsAcceptedAt?: string,
  ): Promise<{ sessionToken: string; publishableKey: string }> => {
    const { data } = await axiosClient.post<{
      data: { sessionToken: string; publishableKey: string };
    }>(API_ENDPOINTS.MOONPAY.SESSION, {
      ...(deviceIp ? { deviceIp } : {}),
      ...(termsAcceptedAt ? { termsAcceptedAt } : {}),
    });
    return data.data;
  },

  /** Best-effort public IP of the end user — MoonPay geolocates the customer's region from it. */
  getPublicIp: async (): Promise<string | undefined> => {
    try {
      const r = await fetch('https://api.ipify.org?format=json');
      const j = await r.json();
      return typeof j?.ip === 'string' ? j.ip : undefined;
    } catch {
      return undefined;
    }
  },

  linkCustomer: async (customerId: string): Promise<MoonpayKycState> => {
    const { data } = await axiosClient.post<{ data: MoonpayKycState }>(
      API_ENDPOINTS.MOONPAY.CUSTOMER_LINK,
      { customerId },
    );
    return data.data;
  },

  getCustomer: async (): Promise<MoonpayKycState> => {
    const { data } = await axiosClient.get<{ data: MoonpayKycState }>(
      API_ENDPOINTS.MOONPAY.CUSTOMER,
    );
    return data.data;
  },

  submitKyc: async (payload: Record<string, any>): Promise<MoonpayKycState> => {
    const { data } = await axiosClient.post<{ data: MoonpayKycState }>(
      API_ENDPOINTS.MOONPAY.KYC_SUBMIT,
      payload,
    );
    return data.data;
  },

  getUploadUrl: async (body: {
    fileType: string;
    mimeType: string;
  }): Promise<{ uploadId: string; uploadUrl: string }> => {
    const { data } = await axiosClient.post<{ data: { uploadId: string; uploadUrl: string } }>(
      API_ENDPOINTS.MOONPAY.KYC_UPLOAD_URL,
      body,
    );
    return data.data;
  },

  confirmFile: async (body: {
    uploadId: string;
    fileType: string;
    side?: string;
  }): Promise<any> => {
    const { data } = await axiosClient.post<{ data: any }>(
      API_ENDPOINTS.MOONPAY.KYC_CONFIRM_FILE,
      body,
    );
    return data.data;
  },

  /** PUT the raw file bytes to MoonPay's presigned URL (not our backend). */
  uploadToPresignedUrl: async (uploadUrl: string, file: File | Blob): Promise<void> => {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': (file as File).type || 'application/octet-stream' },
    });
    if (!res.ok) throw new Error(`Upload failed (${res.status})`);
  },

  getBuyQuote: async (body: {
    accessToken: string;
    amount?: number;
    cryptoAmount?: number;
    fiat?: string;
    paymentMethod?: string;
  }): Promise<any> => {
    const { data } = await axiosClient.post<{ data: any }>(API_ENDPOINTS.MOONPAY.QUOTE_BUY, body);
    return data.data;
  },

  /** Signed BUY widget URL for NON-headless countries (MoonPay owns UI + KYC). */
  getHostedBuyUrl: async (body: {
    amount: number;
    fiat?: string;
  }): Promise<{ url: string; externalTransactionId: string }> => {
    const { data } = await axiosClient.post<{
      data: { url: string; externalTransactionId: string };
    }>(API_ENDPOINTS.MOONPAY.HOSTED_BUY_URL, body);
    return data.data;
  },

  recordDeposit: async (body: {
    moonpayTransactionId?: string;
    amount: number | string;
    fiat?: string;
    cryptoAsset?: string;
    status?: string;
    paymentMethod?: string;
  }): Promise<{ depositId: number; transactionId: string | null; status: string }> => {
    const { data } = await axiosClient.post<{
      data: { depositId: number; transactionId: string | null; status: string };
    }>(API_ENDPOINTS.MOONPAY.DEPOSIT_RECORD, body);
    return data.data;
  },

  getTransaction: async (id: string, accessToken?: string): Promise<any> => {
    const { data } = await axiosClient.get<{ data: any }>(API_ENDPOINTS.MOONPAY.TRANSACTION(id), {
      params: accessToken ? { accessToken } : undefined,
    });
    return data.data;
  },
};
